import { ref, computed, onMounted } from 'vue';
import { PlaceConfig, defaultPlaceConfig } from '@/types/place.types';

export function usePlace() {
  // Place configuration state
  const placeConfig = ref<PlaceConfig>(defaultPlaceConfig);

  // Computed properties
  const placeName = computed(() => placeConfig.value.name);
  const businessName = computed(() => placeConfig.value.business.name);
  const tenantCode = computed(() => placeConfig.value.business.tenantCode);
  const address = computed(() => placeConfig.value.address.formatted);

  // Style properties - light mode
  const primaryColor = computed(() => placeConfig.value.style.light.primary);
  const secondaryColor = computed(() => placeConfig.value.style.light.secondary);
  const accentColor = computed(() => placeConfig.value.style.light.accent);
  const backgroundColor = computed(() => placeConfig.value.style.light.background);
  const textColor = computed(() => placeConfig.value.style.light.text);

  // Style properties - dark mode
  const darkModePrimaryColor = computed(() => placeConfig.value.style.dark.primary);
  const darkModeSecondaryColor = computed(() => placeConfig.value.style.dark.secondary);
  const darkModeAccentColor = computed(() => placeConfig.value.style.dark.accent);
  const darkModeBackgroundColor = computed(() => placeConfig.value.style.dark.background);
  const darkModeTextColor = computed(() => placeConfig.value.style.dark.text);

  // Font
  const font = computed(() => placeConfig.value.style.font);

  // Logo properties
  const logoUrl = computed(() => placeConfig.value.logo.url);
  const logoDarkUrl = computed(() => placeConfig.value.logo.urlDark);
  const logoAspectRatio = computed(() => placeConfig.value.logo.aspectRatio);
  const logoBackgroundColor = computed(() => placeConfig.value.logo.backgroundColor);

  // Determine if dark mode is active based on system preference
  const prefersDarkMode = computed(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  // Get the appropriate logo URL based on dark mode preference
  const currentLogoUrl = computed(() => {
    return prefersDarkMode.value ? logoDarkUrl.value : logoUrl.value;
  });

  // Dynamic style properties based on dark mode
  const currentPrimaryColor = computed(() =>
    prefersDarkMode.value ? placeConfig.value.style.dark.primary : placeConfig.value.style.light.primary
  );

  const currentSecondaryColor = computed(() =>
    prefersDarkMode.value ? placeConfig.value.style.dark.secondary : placeConfig.value.style.light.secondary
  );

  const currentAccentColor = computed(() =>
    prefersDarkMode.value ? placeConfig.value.style.dark.accent : placeConfig.value.style.light.accent
  );

  const currentBackgroundColor = computed(() =>
    prefersDarkMode.value ? placeConfig.value.style.dark.background : placeConfig.value.style.light.background
  );

  const currentTextColor = computed(() =>
    prefersDarkMode.value ? placeConfig.value.style.dark.text : placeConfig.value.style.light.text
  );

  // Initialize from window object
  onMounted(() => {
    if (window.__PLACE_CONFIG__) {
      placeConfig.value = window.__PLACE_CONFIG__ as PlaceConfig;
    } else {
      console.warn('No place configuration found, using default configuration');
    }
  });

  return {
    placeConfig,
    placeName,
    businessName,
    tenantCode,
    address,
    primaryColor,
    secondaryColor,
    accentColor,
    backgroundColor,
    textColor,
    darkModePrimaryColor,
    darkModeSecondaryColor,
    darkModeAccentColor,
    darkModeBackgroundColor,
    darkModeTextColor,
    font,
    logoUrl,
    logoDarkUrl,
    currentLogoUrl,
    logoAspectRatio,
    logoBackgroundColor,
    prefersDarkMode,
    // Dynamic style properties
    currentPrimaryColor,
    currentSecondaryColor,
    currentAccentColor,
    currentBackgroundColor,
    currentTextColor
  };
}

// Type declaration for window object
declare global {
  interface Window {
    __PLACE_CONFIG__?: any;
  }
}
